import { Box, Button, Chip, Typography } from '@mui/material';
import MigrationSuggestionDTO from '@src/adapters/dto/migration/MigrationSuggestionDTO';
import HelpIcon from '@mui/icons-material/Help';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MigrationItemSuggestionStatus } from '@src/constants/MigrationItemSuggestionStatus';
import { useState } from 'react';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import SuggestionAutoComplete from '@src/components/auto-complete/SuggestionAutoComplete';
import Suggestion from './Suggestion';

interface Props {
  suggestions: MigrationSuggestionDTO[];
  highlightItem(suggestionId?: string): void;
  refreshSuggestions(): void;
  selectionCleared(): void;
  suggestionUpdate(
    suggestion: {
      id: string;
      index: number;
    },
    status: MigrationItemSuggestionStatus,
    inventoryId?: string,
    qtyOpen?: number,
    qtyOnHand?: number,
    note?: string,
    alias?: boolean,
    isLoan?: boolean
  ): void;
}

function Suggestions({
  suggestions,
  highlightItem,
  refreshSuggestions,
  selectionCleared,
  suggestionUpdate,
}: Props) {
  const [expanded, setExpanded] = useState(true);
  const [currentSuggestion, setCurrentSuggestion] =
    useState<MigrationSuggestionDTO | null>(null);

  const handleSuggestionSelected = (selectedItem: IdNameDto | null) => {
    if (selectedItem) {
      const selected = suggestions.find((s) => s.id === selectedItem.id);
      if (selected) {
        setCurrentSuggestion(selected);
      }
    } else {
      handleSelectionCleared();
    }
  };

  const handleStatusUpdate = (
    suggestion: MigrationSuggestionDTO,
    status: MigrationItemSuggestionStatus,
    inventoryId?: string,
    qtyOpen?: number,
    qtyOnHand?: number,
    note?: string,
    alias?: boolean,
    isLoan?: boolean
  ) => {
    handleSelectionCleared();
    suggestionUpdate(
      suggestion,
      status,
      inventoryId,
      qtyOpen,
      qtyOnHand,
      note,
      alias,
      isLoan
    );
  };

  const handleSelectionCleared = () => {
    setCurrentSuggestion(null);
    highlightItem(undefined);
    selectionCleared();
  };

  const handleRefeshSuggestions = () => {
    handleSelectionCleared();
    refreshSuggestions();
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(70, 110, 229, 0.05)',
          height: '62px',
          border: '1px solid #E6E7E9',
        })}>
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '200px',
            padding: '8px',
          })}>
          <HelpIcon sx={(theme) => ({ color: theme.palette.warning.main })} />
          <Typography variant="h6">Not Found</Typography>
          <Chip
            label={
              suggestions.filter(
                (s) => s.status === MigrationItemSuggestionStatus.Unchecked
              ).length
            }
            sx={{ background: '#fff', border: '1px solid #E6E7E9' }}
          />
        </Box>
        <Box sx={{ display: 'flex' }}>
          <Button
            onClick={handleRefeshSuggestions}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ height: '30px', width: '94px', borderRadius: '8px' }}>
            <RefreshIcon />
            Refresh
          </Button>
          {expanded && (
            <ExpandLessIcon
              sx={(theme) => ({
                marginLeft: '16px',
                marginRight: '24px',
                color: theme.palette.action.active,
                cursor: 'pointer',
              })}
              onClick={() => setExpanded(false)}
            />
          )}
          {!expanded && (
            <ExpandMoreIcon
              sx={(theme) => ({
                marginLeft: '16px',
                marginRight: '24px',
                color: theme.palette.action.active,
                cursor: 'pointer',
              })}
              onClick={() => setExpanded(true)}
            />
          )}
        </Box>
      </Box>
      {expanded && (
        <Box>
          <Box sx={{ borderBottom: '1px solid grey', padding: '24px' }}>
            <SuggestionAutoComplete
              items={suggestions
                .filter(
                  (s) => s.status === MigrationItemSuggestionStatus.Unchecked
                )
                .map((s) => ({
                  id: s.id,
                  name: s.content,
                }))}
              selection={handleSuggestionSelected}
              selectedItem={
                currentSuggestion
                  ? {
                      id: currentSuggestion.id,
                      name: currentSuggestion.content,
                    }
                  : null
              }
              sx={{ marginBottom: '16px' }}
            />
            {currentSuggestion && (
              <Suggestion
                suggestionId={currentSuggestion.id}
                content={currentSuggestion.content}
                approved={(data) =>
                  handleStatusUpdate(
                    currentSuggestion,
                    MigrationItemSuggestionStatus.Matched,
                    data.inventory.id!,
                    data.qtyOpen,
                    data.qtyOnHand,
                    data.note,
                    data.alias,
                    data.isLoan
                  )
                }
                ignored={() =>
                  handleStatusUpdate(
                    currentSuggestion,
                    MigrationItemSuggestionStatus.Ignored
                  )
                }
                highlightItem={highlightItem}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

export default Suggestions;
